import { Publication } from '@shared/types/publication';
import React, { useState } from 'react';
import { PUBLICATION_TYPE } from '@shared/types/enums';
import { calculateTaxIncludedPrice } from '@shared/utils/utils';
import { formatDateTime } from '@shared/utils/datetime';
import { DATE_YMD } from '@shared/components/constants/common.constants';
import defaultCover from '@shared/assets/images/default-cover.png';
import { useStore } from '@shared/hooks/useStore';
import { formatPublicationTitle } from '@shared/utils/publication';

const RankIcon = ({ ...props }: React.SVGProps<SVGSVGElement>) => (
    <svg id='svg' version='1.1' viewBox='0 0 300 300' width='300' height='300' xmlns='http://www.w3.org/2000/svg' {...props}>
        <svg xmlns='http://www.w3.org/2000/svg'>
            <defs>
                <clipPath id='clip-1'>
                    <rect x='0' y='0' transform='scale(0.14648,0.14648)' width='2048' height='2048' fill='none' />
                </clipPath>
            </defs>
            <g clipPath='url(#clip-1)' fill='none' fillRule='nonzero' stroke='none' strokeWidth='1' strokeLinecap='butt' strokeLinejoin='miter' strokeMiterlimit='10' strokeDasharray='' strokeDashoffset='0' fontFamily='none' fontWeight='none' fontSize='none' textAnchor='inherit' style={{ mixBlendMode: 'normal' }}>
                <path d='M148.53223,7.3938c16.71533,-0.74382 27.56104,14.70571 22.32861,30.25825c-1.4502,4.30957 -5.06836,9.38555 -9.21973,11.42651c7.04004,11.70015 14.51953,23.53154 21.71338,35.16035l23.65576,38.17764c12.08057,-5.071 24.2124,-10.74858 36.19922,-16.07168c7.05469,-3.13257 14.29688,-6.25825 21.29736,-9.48691c-0.92139,-2.10688 -1.56445,-3.63472 -1.90723,-5.96367c-2.45654,-16.71562 16.68164,-27.00352 29.74072,-16.81904c7.75928,6.05127 8.60742,18.39536 2.30713,25.86782c-3.44238,4.08105 -7.57324,5.84194 -12.85986,6.33062l0.00732,163.41431l-130.69629,0.00439l-133.51084,-0.00293c-0.23379,-13.05908 -0.01143,-26.67773 -0.02271,-39.77783l-0.00806,-84.61069c0.00176,-12.848 0.20317,-26.19961 -0.07266,-39.00293c-11.30197,-0.90732 -18.91689,-12.40107 -15.78888,-23.18496c3.19039,-10.99893 15.31031,-16.07725 25.51178,-11.28384c9.72905,4.57163 12.72935,15.60425 8.25879,25.04502l57.55371,25.60415c1.08501,-1.39028 3.52031,-5.4813 4.52842,-7.10889l9.36328,-15.24214c10.6333,-16.96494 21.14941,-34.00298 31.54775,-51.11294c-18.46333,-11.74336 -11.34521,-40.2629 10.073,-41.62061z' fill={props.fill || 'currentColor'} />
                <path d='M18.13799,276.15527l263.51484,0.01025l-0.00439,14.09326l-2.32031,0.00293l-261.25005,-0.02637c-0.0063,-4.41943 -0.14736,-9.73242 0.05991,-14.08008z' fill={props.fill || 'currentColor'} />
            </g>
        </svg>
    </svg>
);

interface PublicationCardProps {
    publication: Publication,
    onQuickView?: (publication: Publication) => void,
    onClick?: (publication: Publication) => void,
    rank?: number
}

const PublicationCard = ({ publication, onQuickView, onClick, rank }: PublicationCardProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const { authStore } = useStore();

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('ja-JP').format(price);
    };

    const shouldShowPrice = () => {
        return !authStore.isLoggedIn || authStore.isStdIndividual;
    };

    const getRankColor = (rank: number) => {
        switch (rank) {
            case 1: return '#d2af37';
            case 2: return '#b3b2b3';
            case 3: return '#a86f49';
            default: return '#209790';
        }
    };

    return (
        <article
            className='w-full cursor-pointer flex flex-col gap-[8px] animate-in fade-in zoom-in-90 duration-300'
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => onClick?.(publication)}
        >
            {/* rank */}
            {rank !== undefined && (
                <div className='relative w-11 h-11 mb-[-8px]'>
                    <RankIcon className='w-full h-full' fill={getRankColor(rank)} />
                    <span className='absolute inset-0 top-4 flex items-center justify-center font-bold text-white text-sm'>
                        {rank}
                    </span>
                </div>
            )}

            {/* Image container */}
            <div className='relative aspect-[3/4] overflow-hidden'>
                <img
                    src={publication.linkImg}
                    alt={publication.title}
                    className='w-full h-full object-fill'
                    loading='lazy'
                    onError={(e) => {
                        e.currentTarget.src = defaultCover;
                    }}
                />

                {/* Quick view overlay */}
                <div
                    className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                >
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onQuickView?.(publication);
                        }}
                        className='hidden md:block bg-[image:linear-gradient(to_right,white_50%,var(--primary)_50%)] bg-[length:200%_100%] bg-right hover:bg-left border border-primary hover:border-black text-white hover:text-black py-2.5 px-6 rounded-[10px] font-semibold text-sm transition-all duration-300'
                    >
                        クイックビュー
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className='flex flex-col gap-1'>
                <h3 className='font-bold text-sm leading-[26px] line-clamp-2'>
                    {formatPublicationTitle(publication)}
                </h3>
                {publication.type === PUBLICATION_TYPE.Book &&
                    (
                        <p className='font-normal text-[11px] leading-[22px] text-text-secondary line-clamp-1'>
                            著者：
                            {publication.author || '不明'}
                        </p>
                    )}
                <p className='font-normal text-[11px] leading-[22px] text-text-secondary'>
                    発行日：
                    {formatDateTime(publication.releaseDate, DATE_YMD, { returnDefault: '未定' })}
                </p>
                {shouldShowPrice() && (
                    <div className='flex items-center justify-end gap-1 mt-auto'>
                        <span className='font-bold text-[13px] text-danger'>
                            ¥
                            {formatPrice(calculateTaxIncludedPrice(publication.price))}
                        </span>
                        <span className='font-normal text-[11px] leading-[22px] text-text-secondary'>税込</span>
                    </div>
                )}
            </div>
        </article>
    );
};

export default PublicationCard;
